/**
 * Shared UploadForm component for upload pages
 * Provides common form logic and UI for both photo and CSV uploads
 */

import React, { useState, useCallback } from 'react';
import { Container, Title, Button, Group, Stack, Alert, Text, Anchor, Table, Textarea } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { Link } from 'react-router-dom';
import Layout from '../Layout';
import RestaurantSelector from './RestaurantSelector';
import UploadModeSelector from './UploadModeSelector';
import ProgressDisplay from './ProgressDisplay';
import StatusBadge from './StatusBadge';
import { useUploadProgress } from '../../hooks/useUploadProgress';
import { prepareUploadFormData, validateUploadForm, UploadFormFields, RestaurantOption } from '../../utils/uploadFormData';
import { FileWithPath } from '@mantine/dropzone';

export interface UploadFormProps {
  title: string;
  uploadType: 'photo' | 'csv';
  fileFieldName: string;
  files: FileWithPath[];
  onFilesChange: (files: FileWithPath[]) => void;
  onUpload: (formData: FormData) => Promise<any>;
  children?: React.ReactNode; // For type-specific content like file dropzone
  noLayout?: boolean; // If true, skip Layout wrapper (for use in admin panel)
}

/**
 * Shared UploadForm component that handles common upload form logic
 * Provides consistent form behavior across upload pages
 */
export const UploadForm: React.FC<UploadFormProps> = ({
  title,
  uploadType,
  fileFieldName,
  files,
  onFilesChange,
  onUpload,
  children,
  noLayout = false
}) => {
  // Form state
  const [selectedRestaurant, setSelectedRestaurant] = useState<string | null>(null);
  const [newRestaurantName, setNewRestaurantName] = useState('');
  const [adminNotes, setAdminNotes] = useState('');
  const [description, setDescription] = useState('');
  const [categoryOrderText, setCategoryOrderText] = useState('');
  const [querySetupText, setQuerySetupText] = useState('');
  const [uploadMode, setUploadMode] = useState<string>('v2');
  const [sourceLanguage, setSourceLanguage] = useState<string>('jp');
  const [loading, setLoading] = useState(false);
  const [clientId, setClientId] = useState<string | null>(null);
  const [restaurants, setRestaurants] = useState<RestaurantOption[]>([]);

  // Progress tracking
  const handleUploadComplete = useCallback((data: any) => {
    onFilesChange([]);
    setLoading(false);
  }, [onFilesChange]);

  const handleUploadError = useCallback((_error: string) => {
    setLoading(false);
  }, []);

  const {
    uploadStatus,
    uploadProgress,
    currentFileStatus,
    currentFileIndex,
    detailedStatus,
    currentStage,
    processedFiles,
    uploadResponse,
    resetProgress
  } = useUploadProgress(
    clientId,
    handleUploadComplete,
    handleUploadError
  );

  // Handle form submission
  const handleUpload = useCallback(async () => {
    const formFields: UploadFormFields = {
      newRestaurantName,
      adminNotes,
      description,
      uploadMode,
      sourceLanguage,
      categoryOrder: categoryOrderText.trim() || undefined,
      querySetup: querySetupText.trim() || undefined,
    };

    // Validate form
    const validation = validateUploadForm(selectedRestaurant, files, formFields);
    if (!validation.isValid) {
      notifications.show({
        title: 'Error',
        message: validation.error,
        color: 'red',
      });
      return;
    }

    setLoading(true);
    resetProgress();
    
    try {
      // Prepare form data
      const formData = prepareUploadFormData(
        files,
        fileFieldName,
        selectedRestaurant,
        restaurants,
        formFields
      );

      // Start upload
      const response = await onUpload(formData);
      
      // Get client ID for progress tracking
      if (response.data?.clientId) {
        setClientId(response.data.clientId);
      }
      
    } catch (error: any) {
      console.error(`Error uploading ${uploadType}:`, error);
      setLoading(false);
      
      notifications.show({
        title: 'Error',
        message: error.response?.data?.message || `Failed to upload ${uploadType}`,
        color: 'red',
      });
    }
  }, [selectedRestaurant, files, newRestaurantName, adminNotes, description, categoryOrderText, querySetupText, uploadMode, sourceLanguage, fileFieldName, onUpload, resetProgress, uploadType]);

  // Get status badge for processed files
  const getStatusBadge = (status: string) => {
    return <StatusBadge status={status} />;
  };

  const content = (
    <Container>
      <Title order={1} mb="md">
        {title}
      </Title>

        {/* Type-specific content (file dropzone, CSV format info, etc.) */}
        {children}

        {/* Restaurant Selection */}
        <RestaurantSelector
          selectedRestaurant={selectedRestaurant}
          onRestaurantChange={setSelectedRestaurant}
          newRestaurantName={newRestaurantName}
          onNewRestaurantNameChange={setNewRestaurantName}
          adminNotes={adminNotes}
          onAdminNotesChange={setAdminNotes}
          description={description}
          onDescriptionChange={setDescription}
          loading={loading}
          onRestaurantsChange={setRestaurants}
        />

        {/* Optional menu settings — same parameters as Edit menu > Menu Metadata */}
        <Stack gap="md" mb="md">
          <Text fw={600} size="sm" c="dimmed">
            Menu settings (optional)
          </Text>
          <Text size="xs" c="dimmed">
            Same fields as Edit menu → Menu Metadata. Welcome modal and header/onboarding images can be set in Edit menu after creation.
          </Text>
          <Textarea
            label="Admin notes"
            description="Shown in Edit menu. Optional notes about this menu."
            placeholder="Add any notes about this menu"
            minRows={3}
            value={adminNotes}
            onChange={(e) => setAdminNotes(e.currentTarget.value)}
            disabled={loading}
          />
          <Textarea
            label="Category order (JSON)"
            description="Optional. Same as Edit menu. Must be valid JSON if provided (e.g. array of [categoryName, index] pairs)."
            placeholder='[["Appetizers", 0], ["Mains", 1]]'
            minRows={4}
            value={categoryOrderText}
            onChange={(e) => setCategoryOrderText(e.currentTarget.value)}
            disabled={loading}
          />
          <Textarea
            label="Query setup (JSON)"
            description="Optional. Same as Edit menu. Language-specific AI query configuration."
            placeholder="{}"
            minRows={6}
            value={querySetupText}
            onChange={(e) => setQuerySetupText(e.currentTarget.value)}
            disabled={loading}
          />
        </Stack>

        {/* Upload Mode Selection */}
        <UploadModeSelector
          uploadMode={uploadMode}
          onUploadModeChange={setUploadMode}
          sourceLanguage={sourceLanguage}
          onSourceLanguageChange={setSourceLanguage}
          loading={loading}
        />

        {/* Progress Display */}
        <ProgressDisplay
          loading={loading}
          uploadStatus={uploadStatus}
          uploadProgress={uploadProgress}
          currentFileStatus={currentFileStatus}
          currentFileIndex={currentFileIndex}
          totalFiles={files.length}
          currentStage={currentStage}
          detailedStatus={detailedStatus}
        />

        {/* Upload Complete Display */}
        {uploadResponse && (
          <Stack mb="md">
            <Alert title="Upload Complete" color="green">
              <Stack gap="xs">
                <Text>
                  <strong>{uploadResponse.restaurantName}</strong> - {uploadResponse.menuName}
                </Text>
                <Text size="sm">
                  Successfully processed {uploadResponse.filesProcessed || uploadResponse.itemsProcessed} {uploadType === 'photo' ? 'file(s)' : 'item(s)'}
                </Text>
                <Text mt="xs">
                  <Anchor component={Link} to={`/menus/v2/${uploadResponse.menuId}`}>
                    View the uploaded menu
                  </Anchor>
                </Text>
              </Stack>
            </Alert>

            {/* Processed Files Table */}
            {processedFiles.length > 0 && (
              <Table striped highlightOnHover>
                <thead>
                  <tr>
                    <th>{uploadType === 'photo' ? 'File' : 'Processing Status'}</th>
                    <th>Status</th>
                    <th>{uploadType === 'photo' ? 'Items Extracted' : 'Items Processed'}</th>
                  </tr>
                </thead>
                <tbody>
                  {processedFiles.map((file, index) => (
                    <tr key={index}>
                      <td>{uploadType === 'photo' ? file.originalName : 'CSV Processing'}</td>
                      <td>{getStatusBadge(file.status)}</td>
                      <td>
                        {file.status === 'complete' ? (
                          <StatusBadge 
                            status="complete" 
                            color="green"
                          >
                            {file.itemsExtracted || file.itemsProcessed} items
                          </StatusBadge>
                        ) : file.status === 'error' ? (
                          <Group gap="xs">
                            <Text size="sm" c="red">{file.error}</Text>
                          </Group>
                        ) : (
                          <Text size="sm" c="dimmed">-</Text>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
          </Stack>
        )}

      {/* Upload Button */}
      <Button
        onClick={handleUpload}
        loading={loading}
        disabled={files.length === 0 || !selectedRestaurant || loading}
        fullWidth
      >
        {loading ? 'Uploading...' : `Upload ${uploadType === 'photo' ? 'Files' : 'CSV'}`}
      </Button>
    </Container>
  );

  // Wrap in Layout only if noLayout is false
  if (noLayout) {
    return content;
  }

  return <Layout>{content}</Layout>;
};

export default UploadForm;
